import * as T from 'three';
import type { RideBiome } from '../biomes';

/**
 * Physically based sky, clouds, aerial-perspective fog and image-based lighting.
 * The sky (Preetham scattering + procedural cumulus) is rendered once per arena into a
 * cube map. That cube is both the visible background and, pre-filtered, the scene's
 * environment light, so reflections, ambient light and fog all agree with the sky.
 */
export interface AtmosphereStyle {
  /** Sun elevation and azimuth in degrees; azimuth 0 = +Z, 90 = +X. */
  elevation: number; azimuth: number;
  turbidity: number; rayleigh: number; mie: number; mieG: number;
  cloudCoverage: number; cloudScale: number; cloudOpacity: number; cloudTint: string;
  /** Fog density per metre, height falloff per metre, base height. */
  fog: number; fogFalloff: number; fogBase: number;
  sunIntensity: number; envIntensity: number; exposure: number;
  /** Multiplies the horizon colour, e.g. warm dust in the canyon. */
  haze: string;
}

export const ATMOSPHERES: Record<RideBiome, AtmosphereStyle> = {
  alpine: { elevation: 31, azimuth: 218, turbidity: 3.2, rayleigh: 1.25, mie: .0042, mieG: .82, cloudCoverage: .56, cloudScale: 1.6, cloudOpacity: .95, cloudTint: '#ffffff', fog: 0.00034, fogFalloff: .0042, fogBase: 120, sunIntensity: 4.4, envIntensity: .95, exposure: .92, haze: '#e8f0f7' },
  canyon: { elevation: 19, azimuth: 250, turbidity: 6.5, rayleigh: .9, mie: .0068, mieG: .86, cloudCoverage: .66, cloudScale: 1.15, cloudOpacity: .85, cloudTint: '#ffe9d6', fog: 0.00047, fogFalloff: .0035, fogBase: 0, sunIntensity: 4.6, envIntensity: .85, exposure: .86, haze: '#ffd9b8' },
  coast: { elevation: 44, azimuth: 205, turbidity: 2.6, rayleigh: 1.45, mie: .0036, mieG: .8, cloudCoverage: .6, cloudScale: 1.3, cloudOpacity: .95, cloudTint: '#ffffff', fog: 0.00032, fogFalloff: .006, fogBase: 0, sunIntensity: 4.6, envIntensity: 1, exposure: .86, haze: '#e6f3f8' },
  meadow: { elevation: 28, azimuth: 120, turbidity: 3.6, rayleigh: 1.2, mie: .0046, mieG: .82, cloudCoverage: .54, cloudScale: 1.45, cloudOpacity: .96, cloudTint: '#ffffff', fog: 0.00039, fogFalloff: .005, fogBase: 0, sunIntensity: 4.4, envIntensity: 1, exposure: .9, haze: '#f2f6f2' },
  harbour: { elevation: 37, azimuth: 160, turbidity: 3, rayleigh: 1.35, mie: .004, mieG: .8, cloudCoverage: .58, cloudScale: 1.25, cloudOpacity: .94, cloudTint: '#f6f9ff', fog: 0.00036, fogFalloff: .0055, fogBase: 0, sunIntensity: 4.5, envIntensity: 1, exposure: .88, haze: '#e8f1fa' },
  park: { elevation: 15, azimuth: 262, turbidity: 4.8, rayleigh: 2.1, mie: .0058, mieG: .88, cloudCoverage: .55, cloudScale: 1.35, cloudOpacity: .94, cloudTint: '#ffd8e6', fog: 0.00043, fogFalloff: .0045, fogBase: 0, sunIntensity: 4.2, envIntensity: 1.05, exposure: .95, haze: '#f4dff0' },
};

// ---------------------------------------------------------------------------
// Aerial perspective: replaces three's fog chunks with height fog plus sun in-scatter.
// Uniform objects are shared by every material: their clone() returns themselves.
const share = <V extends { clone(): unknown }>(value: V): V => { (value as { clone: () => V }).clone = () => value; return value; };
export const FOG_UNIFORMS = {
  fogSunDir: { value: share(new T.Vector3(0, 1, 0)) },
  fogSunColor: { value: share(new T.Color(0, 0, 0)) },
  /** x density, y height falloff, z base height, w sun exponent. Density 0 = classic fog. */
  fogParams: { value: share(new T.Vector4(0, .004, 0, 8)) },
};
let fogInstalled = false;
export function installAtmosphere() {
  if (fogInstalled) return; fogInstalled = true;
  T.ShaderChunk.fog_pars_vertex = `#ifdef USE_FOG\n varying float vFogDepth; varying vec3 vFogView;\n#endif`;
  T.ShaderChunk.fog_vertex = `#ifdef USE_FOG\n vFogDepth = - mvPosition.z; vFogView = mvPosition.xyz;\n#endif`;
  T.ShaderChunk.fog_pars_fragment = /* glsl */`
#ifdef USE_FOG
  uniform vec3 fogColor; varying float vFogDepth; varying vec3 vFogView;
  uniform vec3 fogSunDir; uniform vec3 fogSunColor; uniform vec4 fogParams;
  #ifdef FOG_EXP2
    uniform float fogDensity;
  #else
    uniform float fogNear; uniform float fogFar;
  #endif
#endif`;
  T.ShaderChunk.fog_fragment = /* glsl */`
#ifdef USE_FOG
  #ifdef FOG_EXP2
    if ( fogParams.x > 0.0 ) {
      vec3 rayW = transpose( mat3( viewMatrix ) ) * vFogView;
      float rayLen = length( rayW ); vec3 rd = rayW / max( rayLen, 1e-4 );
      float fb = max( fogParams.y, 1e-5 );
      float ry = rd.y * rayLen * fb;
      float integral = abs( ry ) > 1e-4 ? ( 1.0 - exp( - ry ) ) / ry : 1.0;
      float amount = fogParams.x * rayLen * exp( - ( cameraPosition.y - fogParams.z ) * fb ) * integral;
      float fogFactor = 1.0 - exp( - amount );
      vec3 fogCol = fogColor + fogSunColor * pow( max( dot( rd, fogSunDir ), 0.0 ), fogParams.w );
      gl_FragColor.rgb = mix( gl_FragColor.rgb, fogCol, fogFactor );
    } else {
      float fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );
      gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );
    }
  #else
    float fogFactor = smoothstep( fogNear, fogFar, vFogDepth );
    gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );
  #endif
#endif`;
  const libs = ['basic', 'lambert', 'phong', 'standard', 'physical', 'toon', 'matcap', 'points', 'dashed', 'sprite'] as const;
  for (const lib of libs) Object.assign(T.ShaderLib[lib].uniforms, FOG_UNIFORMS);
  Object.assign(T.UniformsLib.fog, FOG_UNIFORMS);
}

// ---------------------------------------------------------------------------
// Preetham model in JS, used for sun colour and the fog/horizon colour.
const TOTAL_RAYLEIGH = [5.804542996261093e-6, 1.3562911419845635e-5, 3.0265902468824876e-5];
const MIE_CONST = [1.8399918514433978e14, 2.7798023919660528e14, 4.0790479543861094e14];
function scatter(dir: T.Vector3, sun: T.Vector3, s: AtmosphereStyle) {
  const zenithSun = Math.min(1, Math.max(-1, sun.y));
  const sunE = 1000 * Math.max(0, 1 - Math.exp(-((1.6110731556870734 - Math.acos(zenithSun)) / 1.5)));
  const betaR = TOTAL_RAYLEIGH.map(v => v * s.rayleigh);
  const c = .2 * s.turbidity * 10e-18, betaM = MIE_CONST.map(v => .434 * c * v * s.mie);
  const zenith = Math.acos(Math.max(0, dir.y));
  const inverse = 1 / (Math.cos(zenith) + .15 * Math.pow(93.885 - zenith * 180 / Math.PI, -1.253));
  const sR = 8.4e3 * inverse, sM = 1.25e3 * inverse;
  const fex = betaR.map((r, i) => Math.exp(-(r * sR + betaM[i] * sM)));
  const cosTheta = dir.dot(sun);
  const rPhase = 3 / (16 * Math.PI) * (1 + Math.pow(cosTheta * .5 + .5, 2));
  const g2 = s.mieG * s.mieG, mPhase = 1 / (4 * Math.PI) * ((1 - g2) / Math.pow(1 - 2 * s.mieG * cosTheta + g2, 1.5));
  const out = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const ratio = (betaR[i] * rPhase + betaM[i] * mPhase) / (betaR[i] + betaM[i]);
    let lin = Math.pow(sunE * ratio * (1 - fex[i]), 1.5);
    lin *= 1 + (Math.pow(sunE * ratio * fex[i], .5) - 1) * Math.min(1, Math.max(0, Math.pow(1 - sun.y, 5)));
    const tex = (lin + .1 * fex[i]) * .04 + [0, .0003, .00075][i];
    out[i] = Math.pow(tex, 1 / 2.4);
  }
  return { color: new T.Color(out[0], out[1], out[2]), fex };
}
export function sunDirection(s: AtmosphereStyle) {
  const el = T.MathUtils.degToRad(s.elevation), az = T.MathUtils.degToRad(s.azimuth);
  return new T.Vector3(Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el)).normalize();
}

// ---------------------------------------------------------------------------
const SKY_VERTEX = /* glsl */`
varying vec3 vDir;
void main() { vDir = normalize( position ); gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); gl_Position.z = gl_Position.w; }`;
const SKY_FRAGMENT = /* glsl */`
precision highp float;
varying vec3 vDir;
uniform vec3 sunDir; uniform float turbidity; uniform float rayleigh; uniform float mieCoefficient; uniform float mieG;
uniform float sunDisk; uniform float coverage; uniform float cloudScale; uniform float cloudOpacity;
uniform vec3 cloudLit; uniform vec3 cloudShade; uniform vec3 hazeTint; uniform float skyGain; uniform vec2 cloudOffset;
const float pi = 3.141592653589793;
const vec3 totalRayleigh = vec3( 5.804542996261093E-6, 1.3562911419845635E-5, 3.0265902468824876E-5 );
const vec3 MieConst = vec3( 1.8399918514433978E14, 2.7798023919660528E14, 4.0790479543861094E14 );
float hash( vec2 p ) { p = fract( p * vec2( 123.34, 456.21 ) ); p += dot( p, p + 45.32 ); return fract( p.x * p.y ); }
float vnoise( vec2 p ) { vec2 i = floor( p ), f = fract( p ); vec2 u = f * f * ( 3.0 - 2.0 * f );
  return mix( mix( hash( i ), hash( i + vec2( 1, 0 ) ), u.x ), mix( hash( i + vec2( 0, 1 ) ), hash( i + vec2( 1, 1 ) ), u.x ), u.y ); }
float fbm( vec2 p ) { float a = .5, s = 0.0; mat2 m = mat2( 1.6, 1.2, -1.2, 1.6 ); for ( int i = 0; i < 7; i++ ) { s += a * vnoise( p ); p = m * p; a *= .5; } return s; }
void main() {
  vec3 direction = normalize( vDir );
  vec3 up = vec3( 0.0, 1.0, 0.0 );
  float sunE = 1000.0 * max( 0.0, 1.0 - exp( - ( ( 1.6110731556870734 - acos( clamp( sunDir.y, -1.0, 1.0 ) ) ) / 1.5 ) ) );
  vec3 betaR = totalRayleigh * rayleigh;
  vec3 betaM = 0.434 * ( 0.2 * turbidity * 10E-18 ) * MieConst * mieCoefficient;
  float zenithAngle = acos( max( 0.0, dot( up, direction ) ) );
  float inverse = 1.0 / ( cos( zenithAngle ) + 0.15 * pow( 93.885 - ( ( zenithAngle * 180.0 ) / pi ), -1.253 ) );
  vec3 Fex = exp( - ( betaR * 8.4E3 * inverse + betaM * 1.25E3 * inverse ) );
  float cosTheta = dot( direction, sunDir );
  float rPhase = 0.05968310365946075 * ( 1.0 + pow( cosTheta * 0.5 + 0.5, 2.0 ) );
  float g2 = mieG * mieG;
  float mPhase = 0.07957747154594767 * ( ( 1.0 - g2 ) / pow( 1.0 - 2.0 * mieG * cosTheta + g2, 1.5 ) );
  vec3 ratio = ( betaR * rPhase + betaM * mPhase ) / ( betaR + betaM );
  vec3 Lin = pow( sunE * ratio * ( 1.0 - Fex ), vec3( 1.5 ) );
  Lin *= mix( vec3( 1.0 ), pow( sunE * ratio * Fex, vec3( 0.5 ) ), clamp( pow( 1.0 - sunDir.y, 5.0 ), 0.0, 1.0 ) );
  vec3 L0 = vec3( 0.1 ) * Fex;
  L0 += ( sunE * 19000.0 * Fex ) * smoothstep( 0.99995, 0.99997, cosTheta ) * sunDisk;
  vec3 sky = pow( ( Lin + L0 ) * 0.04 + vec3( 0.0, 0.0003, 0.00075 ), vec3( 1.0 / 2.4 ) );
  // Below the horizon the ground is hidden by terrain; keep a soft, hazy continuation.
  float below = smoothstep( 0.02, -0.25, direction.y );
  sky = mix( sky, sky * hazeTint * 0.92, smoothstep( 0.25, -0.02, direction.y ) * 0.55 );
  if ( direction.y > -0.02 ) {
    float h = max( direction.y, 0.0 );
    vec2 p = direction.xz / ( h + 0.09 ) * cloudScale + cloudOffset;
    float n = fbm( p );
    float detail = fbm( p * 3.1 + 7.0 );
    float shape = n * 0.82 + detail * 0.22;
    float density = smoothstep( coverage, coverage + 0.12, shape );
    float core = smoothstep( coverage + 0.06, coverage + 0.32, shape );
    float towardSun = fbm( p + sunDir.xz * 0.22 );
    float lit = clamp( 0.58 + ( n - towardSun ) * 4.5, 0.0, 1.0 );
    float silver = pow( max( cosTheta, 0.0 ), 12.0 ) * ( 1.0 - core ) * 3.0;
    vec3 cloud = mix( cloudShade, cloudLit, lit * ( 1.0 - core * 0.45 ) ) + cloudLit * silver;
    float fade = smoothstep( -0.01, 0.16, direction.y );
    sky = mix( sky, cloud, density * fade * cloudOpacity );
  }
  sky = mix( sky, sky * 0.82, below );
  gl_FragColor = vec4( sky * skyGain, 1.0 );
}`;

export interface SkyResult {
  background: T.Texture; environment: T.Texture;
  sunDir: T.Vector3; sunColor: T.Color; fogColor: T.Color; fogSunColor: T.Color; horizon: T.Color;
  dispose(): void;
}
/** Renders the arena sky once into a cube map and a pre-filtered environment. */
export function buildSky(renderer: T.WebGLRenderer, style: AtmosphereStyle, faceSize: number, seed: number): SkyResult {
  const sun = sunDirection(style);
  const haze = new T.Color(style.haze);
  // Horizon colour averaged around the compass, and the extra glow toward the sun.
  const horizon = new T.Color(0, 0, 0), toward = new T.Color(0, 0, 0);
  for (let i = 0; i < 16; i++) {
    const a = i / 16 * Math.PI * 2, dir = new T.Vector3(Math.sin(a), .035, Math.cos(a)).normalize();
    horizon.add(scatter(dir, sun, style).color.multiplyScalar(1 / 16));
  }
  toward.copy(scatter(new T.Vector3(sun.x, .035, sun.z).normalize(), sun, style).color);
  const skyGain = .62;
  horizon.multiply(haze).multiplyScalar(skyGain);
  toward.multiply(haze).multiplyScalar(skyGain);
  const fogColor = horizon.clone().multiplyScalar(.88), fogSunColor = toward.clone().sub(horizon); fogSunColor.r = Math.max(0, fogSunColor.r); fogSunColor.g = Math.max(0, fogSunColor.g); fogSunColor.b = Math.max(0, fogSunColor.b);
  const { fex } = scatter(sun, sun, style);
  const peak = Math.max(...fex); const sunColor = new T.Color(fex[0] / peak, fex[1] / peak, fex[2] / peak);
  const zenith = scatter(new T.Vector3(0, 1, 0), sun, style).color;
  const tint = new T.Color(style.cloudTint);
  const cloudLit = sunColor.clone().multiply(tint).multiplyScalar(.95 + zenith.r * .4).lerp(new T.Color(1, 1, 1), .25);
  const cloudShade = horizon.clone().lerp(zenith, .45).multiplyScalar(.6).multiply(tint);

  const material = new T.ShaderMaterial({
    vertexShader: SKY_VERTEX, fragmentShader: SKY_FRAGMENT, side: T.BackSide, depthWrite: false, toneMapped: false,
    uniforms: {
      sunDir: { value: sun }, turbidity: { value: style.turbidity }, rayleigh: { value: style.rayleigh }, mieCoefficient: { value: style.mie }, mieG: { value: style.mieG },
      sunDisk: { value: 1 }, coverage: { value: style.cloudCoverage }, cloudScale: { value: style.cloudScale }, cloudOpacity: { value: style.cloudOpacity },
      cloudLit: { value: cloudLit }, cloudShade: { value: cloudShade }, hazeTint: { value: haze }, skyGain: { value: skyGain },
      cloudOffset: { value: new T.Vector2((seed % 97) * .37, (seed % 89) * .53) },
    },
  });
  const skyScene = new T.Scene(); const box = new T.Mesh(new T.BoxGeometry(10, 10, 10), material); skyScene.add(box);
  const makeTarget = () => new T.WebGLCubeRenderTarget(faceSize, { type: T.HalfFloatType, generateMipmaps: true, minFilter: T.LinearMipmapLinearFilter });
  const backgroundTarget = makeTarget(); new T.CubeCamera(.1, 100, backgroundTarget).update(renderer, skyScene);
  material.uniforms.sunDisk.value = 0;
  const envSource = makeTarget(); new T.CubeCamera(.1, 100, envSource).update(renderer, skyScene);
  const pmrem = new T.PMREMGenerator(renderer);
  const envTarget = pmrem.fromCubemap(envSource.texture);
  pmrem.dispose(); envSource.dispose(); box.geometry.dispose(); material.dispose();
  return {
    background: backgroundTarget.texture, environment: envTarget.texture, sunDir: sun, sunColor, fogColor, fogSunColor, horizon,
    dispose() { backgroundTarget.dispose(); envTarget.dispose(); },
  };
}
